import { toPersistentStream } from '@ardoq/rxbeach';
import { distinctUntilChanged, map } from 'rxjs';
import { isEqual } from 'lodash';
import type { FeatureContext } from '@ardoq/features';
import { featureContext$ } from 'streams/featureContext$';
import { ViewIds } from '@ardoq/api-types';
import type {
  ViewpointModeViewKey,
  WorkspaceModeViewKey,
} from './environmentals/viewSettings/types';
import type { MetaInfo } from 'streams/views/mainContent/types';
import {
  getEnabledDefinitions,
  isHiddenByQuickStart,
  toMetaInfo,
} from './metaInfoTabs';
import {
  type ViewDefinition,
  VIEWPOINT_MODE_VIEW_DEFINITIONS,
  WORKSPACE_MODE_VIEW_DEFINITIONS,
} from './metaInfoConsts';

export type ModeViews<ViewId extends ViewIds> = {
  views: MetaInfo[];
  byIds: Record<string, MetaInfo>;
  ids: ViewId[];
};

const getEnabledViews = <Definition extends ViewDefinition>(
  definitions: readonly Definition[],
  featureContext: FeatureContext
): ModeViews<Definition['id']> => {
  const enabledDefinitions = getEnabledDefinitions(
    definitions,
    featureContext
  ).filter(
    ({ isQuickStartView }) =>
      !isHiddenByQuickStart(isQuickStartView, featureContext)
  );
  const views = enabledDefinitions.map(definition =>
    toMetaInfo(definition, featureContext)
  );
  return {
    views,
    byIds: Object.fromEntries(views.map(metaInfo => [metaInfo.id, metaInfo])),
    ids: enabledDefinitions.map(({ id }) => id),
  };
};

const getViewpointModeViews = (
  featureContext: FeatureContext
): ModeViews<ViewpointModeViewKey> =>
  getEnabledViews(VIEWPOINT_MODE_VIEW_DEFINITIONS, featureContext);

const getWorkspaceModeViews = (
  featureContext: FeatureContext
): ModeViews<WorkspaceModeViewKey> =>
  getEnabledViews(WORKSPACE_MODE_VIEW_DEFINITIONS, featureContext);

const NO_VIEWS: ModeViews<never> = { views: [], byIds: {}, ids: [] };

export const viewpointModeViews$ = toPersistentStream<
  ModeViews<ViewpointModeViewKey>
>(
  'viewpointModeViews$',
  featureContext$.pipe(
    map(getViewpointModeViews),
    distinctUntilChanged(isEqual)
  ),
  NO_VIEWS
);

export const workspaceModeViews$ = toPersistentStream<
  ModeViews<WorkspaceModeViewKey>
>(
  'workspaceModeViews$',
  featureContext$.pipe(
    map(getWorkspaceModeViews),
    distinctUntilChanged(isEqual)
  ),
  NO_VIEWS
);
